import React, { useState } from 'react';
import { useCapabilityStore } from '../store/capabilityStore';
import { useGameStore } from '../store/gameStore';
import { useProgressionStore } from '../store/progressionStore';
import { CAPABILITY_DEFINITIONS } from '../utils/capabilityEffects';
import { CapabilityType } from '../types/wargame-capabilities';
import { PrimitiveType } from '../types/progression';
import { Shield, Zap, Radio, Eye, Lock, MessageSquare, Sparkles, Plus, Trash2, LockKeyhole } from 'lucide-react';
import { Tooltip } from './Tooltip';
import { CitationTooltip } from './CitationTooltip';

const ICONS: Record<CapabilityType, React.ReactNode> = {
  MILDEC: <Shield className="w-4 h-4" />,
  NK_Fires: <Zap className="w-4 h-4" />,
  PPP: <Radio className="w-4 h-4" />,
  ISR: <Eye className="w-4 h-4" />,
  OPSEC: <Lock className="w-4 h-4" />,
  MISO: <MessageSquare className="w-4 h-4" />,
  Magic: <Sparkles className="w-4 h-4" />
};

const CAPABILITY_TO_PRIMITIVE: Record<CapabilityType, PrimitiveType> = {
  'MILDEC': 'DeceptiveActivities',
  'NK_Fires': 'TechnicalActivities',
  'PPP': 'PublicAffairs',
  'ISR': 'Intel',
  'OPSEC': 'OPSEC',
  'MISO': 'PsyOp',
  'Magic': 'EW'
};

export const CapabilityPanel: React.FC = () => {
  const { capabilitySlots, maxCapabilitySlots, placedCapabilities, addCapabilitySlot, removeCapability, placeCapability } = useCapabilityStore();
  const { performAction, ap } = useGameStore();
  const { unlockedPrimitives } = useProgressionStore();
  const [selectedCap, setSelectedCap] = useState<CapabilityType | null>(null);

  const handleTrade = () => {
    if (ap >= 2) {
      performAction('Trade_Capacity');
      addCapabilitySlot();
    }
  };

  const handlePlace = (type: CapabilityType) => {
    setSelectedCap(type);
  };

  const isLocked = (type: CapabilityType) => {
    const primitive = CAPABILITY_TO_PRIMITIVE[type];
    return !unlockedPrimitives.includes(primitive);
  };

  return (
    <div className="bg-slate-900 border-l border-slate-800 w-full flex flex-col h-full">
      <div className="p-4 border-b border-slate-800">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Shield className="w-5 h-5 text-ops-blue" />
          Capabilities
        </h2>
        <div className="mt-4 flex justify-between items-center bg-slate-800 p-2 rounded">
          <div className="text-sm text-slate-300">
            Slots: <span className="text-white font-bold">{capabilitySlots}/{maxCapabilitySlots}</span>
          </div>
          <button 
            onClick={handleTrade}
            disabled={ap < 2}
            className="text-xs bg-ops-blue hover:bg-blue-600 disabled:opacity-50 text-white px-2 py-1 rounded flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Trade 2 AP
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Available Capabilities */}
        <div className="space-y-3">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Available</h3>
          {Object.values(CAPABILITY_DEFINITIONS).map((cap) => {
            const locked = isLocked(cap.type);
            const primitive = CAPABILITY_TO_PRIMITIVE[cap.type];
            
            return (
              <CitationTooltip key={cap.type} capabilityType={cap.type}>
                <div 
                  className={`relative p-3 rounded border transition-all ${
                    locked 
                      ? 'bg-slate-900 border-slate-800 opacity-50 cursor-not-allowed' 
                      : selectedCap === cap.type 
                        ? 'bg-ops-blue/20 border-ops-blue cursor-pointer' 
                        : 'bg-slate-800 border-slate-700 hover:border-slate-600 cursor-pointer'
                  }`}
                  onClick={() => !locked && handlePlace(cap.type)}
                >
                  {locked && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-10 rounded">
                      <Tooltip content={`Requires mastery of ${primitive}`}>
                        <LockKeyhole className="w-6 h-6 text-slate-400" />
                      </Tooltip>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded ${locked ? 'bg-slate-800 text-slate-500' : 'bg-slate-700 text-ops-blue'}`}>
                        {ICONS[cap.type]}
                      </div>
                      <div>
                        <div className="font-bold text-sm text-white">{cap.type}</div>
                        <div className="text-xs text-slate-400">{cap.placement}</div>
                      </div>
                    </div>
                    <div className="text-xs font-mono bg-slate-900 px-2 py-1 rounded text-slate-300">
                      Cost: {cap.cost}
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    {cap.effects.map((effect, i) => (
                      <div key={i} className="text-xs text-slate-400 flex items-start gap-1">
                        <span className="text-ops-blue/50 mt-0.5">•</span>
                        {effect}
                      </div>
                    ))}
                  </div>
                </div>
              </CitationTooltip>
            );
          })}
        </div>

        {/* Placed Capabilities */}
        {placedCapabilities.length > 0 && (
          <div className="space-y-3 pt-4 border-t border-slate-800">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Deployed</h3>
            {placedCapabilities.map((placed) => (
              <div key={placed.capabilityId} className="bg-slate-800/50 border border-slate-700 p-3 rounded flex justify-between items-center">
                <div className="flex items-center gap-2">
                  {ICONS[placed.type]}
                  <div>
                    <div className="text-sm font-medium text-white">{placed.type}</div>
                    <div className="text-xs text-slate-400">Turn {placed.turn}</div>
                  </div>
                </div>
                <button 
                  onClick={() => removeCapability(placed.capabilityId)}
                  className="text-slate-500 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
